import React, { useState, useContext } from "react";
import { Modal, Button, Badge, Form } from "react-bootstrap";
import Select from "react-select";
import NotyfContext from "../../../../contexts/NotyfContext";
import axios from "axios";
import { apiConfig } from "../../../../config";
import useAuth from "../../../../hooks/useAuth";
import usePermissions from "../../../../hooks/usePermissions";

const ACCESS_LEVEL_LABELS = {
  0: "Web",
  1: "Web & LAN",
  2: "Custom",
};

const Configure8021XCredentialModal = ({
  openModals,
  toggle,
  passedData,
  dispatch,
  setRefreshData,
}) => {
  const notyf = useContext(NotyfContext);
  const [type] = useState("success");
  const [duration] = useState("30000");
  const [ripple] = useState(true);
  const [dismissible] = useState(true);
  const [positionX] = useState("right");
  const [positionY] = useState("top");

  const { userAccessToken } = useAuth();
  const bearerToken = `Bearer ${userAccessToken}`;

  const canUseCustomVlan = usePermissions("edit:custom_vlan:all");

  // `customvlan` is a 0/1 flag indicating a custom vlan is in use; the
  // actual vlan number is still stored in the regular per-ssid vlan field.
  const getCurrentCustomVlan = (data) =>
    data?.customvlan ? data?.uservlans?.[0]?.vlan ?? undefined : undefined;

  const [accessLevelNum, setAccessLevelNum] = useState(
    passedData?.userlevel ?? null
  );
  const [customVlanId, setCustomVlanId] = useState(
    getCurrentCustomVlan(passedData)
  );

  React.useEffect(() => {
    setAccessLevelNum(passedData?.userlevel ?? null);
    setCustomVlanId(getCurrentCustomVlan(passedData));
  }, [passedData]);

  const accessLevelOptions =
    canUseCustomVlan[1] === true
      ? [
          { value: 2, label: "Custom" },
          { value: 1, label: "Web & LAN (Vlan: Room Vlan)" },
          { value: 0, label: "Web Only (Legacy) (Vlan: 769)" },
        ]
      : [
          { value: 1, label: "Web & LAN (Vlan: Room Vlan)" },
          { value: 0, label: "Web Only (Legacy) (Vlan: 769)" },
        ];

  const isCustomSelected = accessLevelNum === 2;
  const hasValidCustomVlan =
    customVlanId !== undefined &&
    customVlanId !== "" &&
    !Number.isNaN(Number(customVlanId));
  const canSubmitAccessChange =
    accessLevelNum !== null &&
    accessLevelNum !== passedData?.userlevel &&
    (!isCustomSelected || hasValidCustomVlan);
  const clearDevices = (data) => {
    const axiosConfig = {
      headers: { Authorization: bearerToken },
    };
    axios
      .patch(
        apiConfig.credentialsApiUrl +
          `users/user/${data.username}/macaddresses/`,
        null,
        axiosConfig
      )
      .then(function (response) {
        if (response.data.success === 1) {
          notyf.open({
            type,
            message: response.data.message,
            duration,
            ripple,
            dismissible,
            position: {
              x: positionX,
              y: positionY,
            },
          });
        } else {
          notyf.open({
            type: "warning",
            message: response.data.error,
            duration,
            ripple,
            dismissible,
            position: {
              x: positionX,
              y: positionY,
            },
          });
        }
      })
      .catch(function (error) {
        notyf.open({
          type: "warning",
          message: error.response.data.error,
          duration,
          ripple,
          dismissible,
          position: {
            x: positionX,
            y: positionY,
          },
        });
      });
  };
  const changeAccess = (data, newUserLevel, newCustomVlan) => {
    const setUserLevel = { userlevel: newUserLevel };
    if (newUserLevel === 2) {
      setUserLevel.custom_vlan = newCustomVlan;
    }
    const axiosConfig = {
      headers: { Authorization: bearerToken },
    };
    axios
      .patch(
        apiConfig.credentialsApiUrl + `users/user/${data.username}/userlevel`,
        setUserLevel,
        axiosConfig
      )
      .then(function (response) {
        if (response.data.success === 1) {
          notyf.open({
            type,
            message: response.data.message,
            duration,
            ripple,
            dismissible,
            position: {
              x: positionX,
              y: positionY,
            },
          });
        } else {
          notyf.open({
            type: "warning",
            message: response.data.error,
            duration,
            ripple,
            dismissible,
            position: {
              x: positionX,
              y: positionY,
            },
          });
        }
      })
      .catch(function (error) {
        notyf.open({
          type: "warning",
          message: error.response.data.error,
          duration,
          ripple,
          dismissible,
          position: {
            x: positionX,
            y: positionY,
          },
        });
      });
  };
  return (
    <Modal
      show={openModals["configureDot11Credential"]}
      onHide={() => toggle("configureDot11Credential")}
      centered
    >
      <Modal.Header closeButton>
        <b>Admin Function:</b>&nbsp; Configure Dot11 Credential
      </Modal.Header>
      <Modal.Body className="m-3">
        <p className="text-left my-0">
          User <b>{passedData.username}</b> currently has{" "}
          <Badge className="btn-gradient">
            {passedData?.devicevendor === ""
              ? 0
              : passedData?.devicevendor?.split("#~!~#")?.length}
            /5
          </Badge>{" "}
          devices connected.
          <br />
          <br />
          <Button
            className="w-100"
            onClick={() => {
              clearDevices(passedData);
              dispatch(setRefreshData());
              toggle("configureDot11Credential");
            }}
            disabled={passedData?.devicevendor === "" ? true : false}
          >
            Click here to clear devices
          </Button>
          <br />
          <br />
          User <b>{passedData.username}</b> currently has{" "}
          <Badge className="btn-gradient">
            {ACCESS_LEVEL_LABELS[passedData.userlevel] ?? "Unknown"}
          </Badge>{" "}
          access.
        </p>

        <Form.Group className="mb-3">
          <Form.Label>Access Level</Form.Label>
          <Select
            className="react-select-container"
            classNamePrefix="react-select"
            options={accessLevelOptions}
            value={
              accessLevelOptions.find((o) => o.value === accessLevelNum) ?? null
            }
            onChange={(e) => setAccessLevelNum(e.value)}
          />
        </Form.Group>

        {isCustomSelected && (
          <Form.Group className="mb-3">
            <Form.Label>Custom Vlan</Form.Label>
            <Form.Control
              type="number"
              placeholder="Vlan ID"
              value={customVlanId ?? ""}
              onChange={(e) => setCustomVlanId(e.target.value)}
              isInvalid={!hasValidCustomVlan}
              required
            />
            <Form.Control.Feedback type="invalid">
              Please provide a valid custom vlan
            </Form.Control.Feedback>
          </Form.Group>
        )}

        <Button
          className="w-100"
          disabled={!canSubmitAccessChange}
          onClick={() => {
            changeAccess(passedData, accessLevelNum, customVlanId);
            setTimeout(() => dispatch(setRefreshData()), 2000);
            toggle("configureDot11Credential");
          }}
        >
          Update access
        </Button>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={() => toggle("configureDot11Credential")}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default Configure8021XCredentialModal;
